// src/components/TransactionDetails.js
import React, { useState } from "react";
import { useFinanceData } from "../hooks/useFinanceData";
import moment from "moment";
import EditExpenseForm from "./EditExpenseForm";
import EditIncomeForm from "./EditIncomeForm";

// Add new DeleteConfirmDialog component
const DeleteConfirmDialog = ({ item, onConfirm, onCancel }) => {
  const [deleteType, setDeleteType] = useState("all");
  
  const isRecurring = item.recurrenceFrequency && item.recurrenceFrequency !== 'none';
  
  // If it's not recurring, we don't need to show options
  if (!isRecurring) {
    return (
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1002
      }}>
        <div style={{
          backgroundColor: "white",
          borderRadius: "0.5rem",
          width: "90%",
          maxWidth: "400px",
          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          padding: "1.5rem"
        }}>
          <h3 style={{ marginTop: 0 }}>Delete Transaction</h3>
          <p>Are you sure you want to delete "{item.title}"?</p>
          <div style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "0.5rem",
            marginTop: "1.5rem"
          }}>
            <button
              onClick={onCancel}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor: "#f3f4f6",
                border: "none",
                borderRadius: "0.375rem",
                cursor: "pointer"
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm(item, "all", item.date)}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor: "#ef4444",
                color: "white",
                border: "none",
                borderRadius: "0.375rem",
                cursor: "pointer"
              }}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // For recurring items we show all options
  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1002
    }}>
      <div style={{
        backgroundColor: "white",
        borderRadius: "0.5rem",
        width: "90%",
        maxWidth: "400px",
        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
        padding: "1.5rem"
      }}>
        <h3 style={{ marginTop: 0 }}>Delete Transaction</h3>
        <p>How would you like to delete "{item.title}"?</p>
        
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem",
          marginTop: "1rem"
        }}>
          <label style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            cursor: "pointer",
            padding: "0.5rem",
            border: "1px solid #e5e7eb",
            borderRadius: "0.375rem",
            backgroundColor: deleteType === "single" ? "#f0f9ff" : "transparent"
          }}>
            <input
              type="radio"
              name="deleteType"
              value="single"
              checked={deleteType === "single"}
              onChange={() => setDeleteType("single")}
            />
            <span>Delete this occurrence only</span>
          </label>
          
          <label style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            cursor: "pointer",
            padding: "0.5rem",
            border: "1px solid #e5e7eb",
            borderRadius: "0.375rem",
            backgroundColor: deleteType === "future" ? "#f0f9ff" : "transparent"
          }}>
            <input
              type="radio"
              name="deleteType"
              value="future"
              checked={deleteType === "future"}
              onChange={() => setDeleteType("future")}
            />
            <span>Delete this and all future occurrences</span>
          </label>
          
          <label style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            cursor: "pointer",
            padding: "0.5rem",
            border: "1px solid #e5e7eb",
            borderRadius: "0.375rem",
            backgroundColor: deleteType === "all" ? "#f0f9ff" : "transparent"
          }}>
            <input
              type="radio"
              name="deleteType"
              value="all"
              checked={deleteType === "all"}
              onChange={() => setDeleteType("all")}
            />
            <span>Delete all occurrences</span>
          </label>
        </div>
        
        <div style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "0.5rem",
          marginTop: "1.5rem"
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: "#f3f4f6",
              border: "none",
              borderRadius: "0.375rem",
              cursor: "pointer"
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(item, deleteType, item.date)}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: "#ef4444",
              color: "white",
              border: "none",
              borderRadius: "0.375rem",
              cursor: "pointer"
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const TransactionDetails = ({ item, onClose, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const { 
    markAsPaid, 
    unmarkAsPaid, 
    isPaidOccurrence,
    formatAmount, 
    getCategoryInfo,
    getCategoryColor,
    getCategoryEmoji,
    editItem
  } = useFinanceData();

  // Get notification preferences from item
  const getNotificationPreference = (item) => {
    // Always return the base item's notification preference
    // This ensures we're not using occurrence-specific preferences
    if (!item.notificationPreference) return ['off'];
    
    if (Array.isArray(item.notificationPreference)) {
      return item.notificationPreference;
    }
    
    // Convert from object format to array format if needed
    const notificationArray = Object.entries(item.notificationPreference)
      .map(([_, value]) => value)
      .filter(value => value !== 'off');
      
    return notificationArray.length > 0 ? notificationArray : ['off'];
  };
  
  // Use the new isPaidOccurrence function directly from the hook
  const checkIfPaid = (item, date) => {
    return isPaidOccurrence(item, date);
  };

  // Handle form submission
  const handleSubmit = async (updatedItem, updateType) => {
    try {
      console.log('TransactionDetails handling submit', { updatedItem, updateType });
      
      // Call editItem directly from useFinanceData
      // const result = await editItem(updatedItem, updateType);
      
      // Notify parent component if needed
      // onUpdate(result);
      
      setIsEditing(false);
      onClose();
    } catch (error) {
      console.error('Error updating transaction:', error);
      alert('Failed to update transaction. Please try again.');
    }
  };

  // Open the delete confirmation dialog
  const openDeleteConfirm = () => {
    setShowDeleteConfirm(true);
  };

  // Handle transaction deletion confirmation
  const handleDeleteConfirm = (item, deleteType, deleteDate) => {
    console.log('TransactionDetails handling delete:', {
      itemId: item?.id,
      itemType: item?.type,
      deleteType,
      deleteDate,
      itemKeys: item ? Object.keys(item) : 'item is null'
    });
    
    // Ensure item has a type property before deletion
    if (item && !item.type) {
      console.error('Item missing type property before deletion!', item);
      // Attempt to add type based on available properties
      if (item.budgetCategoryId) {
        console.log('Setting missing type to "expense" based on budgetCategoryId property');
        item.type = 'expense';
      } else if (item.amount > 0 && !item.budgetCategoryId) {
        console.log('Setting missing type to "income" based on positive amount');
        item.type = 'income';
      } else {
        console.log('Cannot determine type, available properties:', Object.keys(item));
      }
    }
    
    onDelete(item, deleteType, deleteDate);
    setShowDeleteConfirm(false);
    onClose();
  };

  // Show confirmation dialog for amount
  const showAmountConfirmation = (occurrenceDate) => {
    const dateStr = moment(occurrenceDate).format('YYYY-MM-DD');
    const currentAmount = item.occurrenceExceptions?.[dateStr]?.amount || item.amount;
    
    // Convert amount appropriately based on if it's stored in cents or dollars
    // In the web version, amounts are typically stored in dollars already in the UI
    const formattedAmount = Math.abs(currentAmount);

    const newAmount = window.prompt(
      'Please confirm or update the amount:',
      formattedAmount.toString()
    );

    if (!newAmount) return;

    const amount = parseFloat(newAmount);
    if (isNaN(amount)) {
      alert('Please enter a valid amount');
      return;
    }

    try {
      // Use the new markAsPaid function
      markAsPaid(item.id, occurrenceDate, {
        amount: amount,
      }).then(updatedItem => {
        onUpdate(updatedItem);
        onClose();
      });
    } catch (error) {
      console.error('Error marking as paid:', error);
      alert('Failed to update paid status. Please try again.');
    }
  };

  // Toggle paid status
  const handleTogglePaid = async (occurrenceDate) => {
    if (checkIfPaid(item, occurrenceDate)) {
      try {
        // Use the new unmarkAsPaid function
        const success = await unmarkAsPaid(item.id, occurrenceDate);
        if (success) {
          onUpdate(true); // Just signal that an update happened
          onClose();
        }
      } catch (error) {
        console.error('Error unmarking as paid:', error);
        alert('Failed to update paid status. Please try again.');
      }
    } else {
      showAmountConfirmation(occurrenceDate);
    }
  };

  // Format recurrence text
  const formatRecurrenceText = (frequency, period) => {
    const freq = Number(frequency);
    if (period === 'none' || !period) return 'One-time';
    
    if (freq === 1) {
      switch (period) {
        case 'days': return 'Daily';
        case 'weeks': return 'Weekly';
        case 'months': return 'Monthly';
        default: return `Every ${period}`;
      }
    }

    const periodSingular = period.endsWith('s') ? period.slice(0, -1) : period;
    return `Every ${freq} ${periodSingular}s`;
  };

  // Format notification text
  const formatNotificationText = (preferences) => {
    if (!preferences || preferences.length === 0) return 'None';
    
    if (preferences.includes('off')) return 'None';
    
    return preferences.map(days => `${days} days before`).join(', ');
  };

  // Get category details
  const getCategoryName = (categoryId) => {
    const category = getCategoryInfo(categoryId);
    return category ? category.title : "Uncategorized";
  };

  // Helper to get emoji for item
  const getEmoji = (item) => {
    if (item.emoji) return item.emoji;
    if (item.type === 'expense' && item.budgetCategoryId) {
      return getCategoryEmoji(item.budgetCategoryId);
    }
    return item.type === 'income' ? '💰' : '💸';
  };

  // Handle form cancel
  const handleFormCancel = () => {
    setIsEditing(false);
  };
  
  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: "white",
        borderRadius: "0.5rem",
        width: "90%",
        maxWidth: "500px",
        maxHeight: "90vh",
        overflow: "auto",
        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
        position: "relative"
      }}>
        {/* Header */}
        <div style={{
          padding: "1.5rem",
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: "bold" }}>
            {getEmoji(item)} {item.title}
          </h2>
          <button 
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.5rem",
              cursor: "pointer",
              padding: "0.25rem"
            }}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: "1.5rem" }}>
          {/* Amount */}
          <div style={{ marginBottom: "1.25rem" }}>
            <div style={{ color: "#6b7280", fontSize: "0.875rem", marginBottom: "0.25rem" }}>
              Amount
            </div>
            <div style={{ 
              fontSize: "1.125rem", 
              fontWeight: "bold",
              color: item.type === 'income' ? "#047857" : "#dc2626" 
            }}>
              {formatAmount(item.amount, { showSign: true })}
            </div>
          </div>

          {/* Date */}
          <div style={{ marginBottom: "1.25rem" }}>
            <div style={{ color: "#6b7280", fontSize: "0.875rem", marginBottom: "0.25rem" }}>
              Paying On
            </div>
            <div style={{ fontSize: "1.125rem" }}>
              {moment(item.date).format('MMMM D, YYYY')}
            </div>
          </div>

          {/* Notifications - only for expenses */}
          {item.type === 'expense' && (
            <div style={{ marginBottom: "1.25rem" }}>
              <div style={{ color: "#6b7280", fontSize: "0.875rem", marginBottom: "0.25rem" }}>
                Notifications
              </div>
              <div style={{ fontSize: "1.125rem" }}>
                {formatNotificationText(getNotificationPreference(item))}
              </div>
            </div>
          )}

          {/* Recurrence - if applicable */}
          {item.recurrenceFrequency && item.recurrenceFrequency !== 'none' && (
            <div style={{ marginBottom: "1.25rem" }}>
              <div style={{ color: "#6b7280", fontSize: "0.875rem", marginBottom: "0.25rem" }}>
                Recurrence
              </div>
              <div style={{ fontSize: "1.125rem" }}>
                {formatRecurrenceText(item.recurrenceFrequency, item.recurrencePeriod)}
              </div>
            </div>
          )}

          {/* Category - only for expenses */}
          {item.type === 'expense' && item.budgetCategoryId && (
            <div style={{ marginBottom: "1.25rem" }}>
              <div style={{ color: "#6b7280", fontSize: "0.875rem", marginBottom: "0.25rem" }}>
                Category
              </div>
              <div style={{
                display: "inline-block",
                padding: "0.375rem 0.75rem",
                borderRadius: "0.375rem",
                backgroundColor: `${getCategoryColor(item.budgetCategoryId)}20`,
                border: `1px solid ${getCategoryColor(item.budgetCategoryId)}`,
                fontSize: "0.875rem"
              }}>
                {getCategoryName(item.budgetCategoryId)}
              </div>
            </div>
          )}

          {/* Mark as paid toggle */}
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "1rem",
            backgroundColor: "#f9fafb",
            borderRadius: "0.375rem",
            marginBottom: "1.25rem"
          }}>
            <div style={{ fontWeight: "bold" }}>Mark as paid</div>
            <label className="switch" style={{
              position: "relative",
              display: "inline-block",
              width: "60px",
              height: "34px"
            }}>
              <input 
                type="checkbox" 
                checked={checkIfPaid(item, item.date)}
                onChange={() => handleTogglePaid(item.date)}
                style={{
                  opacity: 0,
                  width: 0,
                  height: 0
                }}
              />
              <span style={{
                position: "absolute",
                cursor: "pointer",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: checkIfPaid(item, item.date) ? "#2563eb" : "#e5e7eb",
                transition: "0.4s",
                borderRadius: "34px"
              }}>
                <span style={{
                  position: "absolute",
                  content: "",
                  height: "26px",
                  width: "26px",
                  left: checkIfPaid(item, item.date) ? "30px" : "4px",
                  bottom: "4px",
                  backgroundColor: "white",
                  transition: "0.4s",
                  borderRadius: "50%"
                }}></span>
              </span>
            </label>
          </div>

          {/* Notes */}
          {item.note && (
            <div style={{ marginBottom: "1.25rem" }}>
              <div style={{ color: "#6b7280", fontSize: "0.875rem", marginBottom: "0.25rem" }}>
                Note
              </div>
              <div style={{
                padding: "0.75rem",
                backgroundColor: "#f9fafb",
                borderRadius: "0.375rem",
                fontSize: "1rem"
              }}>
                {item.note}
              </div>
            </div>
          )}
        </div>

        {/* Footer buttons */}
        <div style={{
          borderTop: "1px solid #e5e7eb",
          padding: "1rem",
          display: "flex",
          justifyContent: "space-between",
          gap: "0.5rem"
        }}>
          <button
            onClick={openDeleteConfirm}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: "#fee2e2",
              color: "#dc2626",
              border: "none",
              borderRadius: "0.375rem",
              cursor: "pointer",
              fontWeight: "medium",
              flexGrow: 1
            }}
          >
            Delete
          </button>
          <button
            onClick={() => setIsEditing(true)}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: "#2563eb",
              color: "white",
              border: "none",
              borderRadius: "0.375rem",
              cursor: "pointer",
              fontWeight: "medium",
              flexGrow: 2
            }}
          >
            Edit
          </button>
        </div>

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <DeleteConfirmDialog 
            item={item}
            onConfirm={handleDeleteConfirm}
            onCancel={() => setShowDeleteConfirm(false)}
          />
        )}

        {/* Edit Form Modal */}
        {isEditing && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1001
          }}>
            <div style={{
              backgroundColor: "white",
              borderRadius: "0.5rem",
              width: "90%",
              maxWidth: "500px",
              maxHeight: "90vh",
              overflow: "auto",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
            }}>
              {/* Conditionally render EditExpenseForm or EditIncomeForm based on transaction type */}
              {item.type === 'expense' ? (
                <EditExpenseForm
                  initialValues={{
                    ...item,
                    amount: item.amount,
                    date: moment(item.date),
                    budgetCategoryId: item.budgetCategoryId,
                    emoji: item.emoji,
                    recurrenceFrequency: item.recurrenceFrequency === 'none' || !item.recurrenceFrequency ? null : item.recurrenceFrequency,
                    recurrencePeriod: item.recurrenceFrequency === 'none' || !item.recurrenceFrequency ? 'none' : (item.recurrencePeriod || 'none'),
                    notificationPreference: getNotificationPreference(item),
                  }}
                  onSubmit={handleSubmit}
                  onCancel={handleFormCancel}
                  onDelete={openDeleteConfirm}
                  useDirectUpdate={true}
                />
              ) : item.type === 'income' ? (
                <EditIncomeForm
                initialValues={{
                  id: item.id,
                  title: item.title,
                  amount: item.amount,
                  date: moment(item.date),
                  emoji: item.emoji || '💰',
                  recurrenceFrequency: item.recurrenceFrequency === 'none' || !item.recurrenceFrequency ? null : item.recurrenceFrequency,
                  recurrencePeriod: item.recurrenceFrequency === 'none' || !item.recurrenceFrequency ? 'none' : (item.recurrencePeriod || 'months'),
                  // Only include fields that are relevant to income items
                  createdAt: item.createdAt,
                  occurrenceExceptions: item.occurrenceExceptions,
                  isPaid: item.isPaid,
                }}
                  onSubmit={handleSubmit}
                  onCancel={handleFormCancel}
                  onDelete={openDeleteConfirm}
                  useDirectUpdate={true}
                />
              ) : (
                <div style={{ padding: "1.5rem" }}>
                  <h3>Edit Transaction</h3>
                  <p>Edit form for {item.type} transactions would go here</p>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1rem" }}>
                    <button
                      onClick={handleFormCancel}
                      style={{
                        padding: "0.5rem 1rem",
                        backgroundColor: "#f3f4f6",
                        border: "none",
                        borderRadius: "0.375rem",
                        cursor: "pointer"
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionDetails;